import { LogIn, Menu, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ThemeToggle } from '../../components/ui/ThemeToggle';
import { useAuth } from '../../contexts/auth';
import { loaders, prefetchMemberArea } from '../../routes/lazy-pages';
import { homeAnchors, paths, publicPaths } from '../../routes/paths';
import { usePublicSiteContent } from '../../services/public-queries';
import { warmHbaac } from '../hbaac-warm';

/**
 * A menu entry. "Tin tức" and "Tuyển thành viên" open their own pages; the
 * others are sections of the home page. Always a router Link, never a plain <a href="/#…">: from
 * another page that would reload the whole site, which is what made moving
 * between pages feel like pressing F5. PublicLayout scrolls to the hash.
 */
function NavTarget({ id, ...rest }: { id: string } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  const to = id === 'news' ? publicPaths.news : id === 'join' ? publicPaths.join : { pathname: publicPaths.home, hash: `#${id}` };
  return <Link to={to} {...rest} />;
}

const HBAAC_LOGO_LINK =
  'flex items-center rounded-lg transition-transform duration-200 hover:scale-[1.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:focus-visible:ring-brand-400 focus-visible:ring-offset-2 motion-reduce:transition-none';

/**
 * Header for the public site.
 *
 * On the home page the links are anchors into the one-page layout; anywhere
 * else they have to navigate home first, so they become `/#anchor`. Getting
 * that wrong is the usual bug with anchor navigation on a multi-page site.
 */
export function PublicHeader() {
  const { pathname } = useLocation();
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // A signed-in member reading the news will likely go back to the portal.
  useEffect(() => {
    if (token) prefetchMemberArea();
  }, [token]);
  // Start the download on hover so the click lands on a loaded screen.
  const warmLogin = () => void (token ? loaders.AppLayout() : loaders.LoginScreen()).catch(() => undefined);
  const { content } = usePublicSiteContent();

  // The recruitment link only exists while its section does.
  const anchors = homeAnchors.filter((a) => a.id !== 'join' || Boolean(content?.recruitment.enabled));

  const onHome = pathname === publicPaths.home;
  // Links to the HBAAC page fetch it ahead and open it inside a view transition (Plan AO).
  const toHbaac = {
    to: publicPaths.competition,
    viewTransition: true,
    onPointerEnter: () => warmHbaac(content?.competition.keyVisual),
    onTouchStart: () => warmHbaac(content?.competition.keyVisual),
    onFocus: () => warmHbaac(content?.competition.keyVisual),
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  // The phone menu is a disclosure (APG): Esc closes it and gives focus back to its button.
  const toggle = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      toggle.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);


  // On the home page, the section in the middle of the screen is the "current" link.
  const [activeId, setActiveId] = useState<string | null>(null);
  useEffect(() => {
    if (!onHome || !('IntersectionObserver' in window)) return setActiveId(null);
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) setActiveId(e.target.id);
      },
      { rootMargin: '-45% 0px -50% 0px' },
    );
    // Sections without a menu item are watched too, so no item stays lit while they are on screen.
    const ids = [...homeAnchors.map((a) => a.id), 'activities', 'hbaac', 'contact', 'hero'];
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [onHome, content]);

  const current = onHome ? activeId : pathname.startsWith(publicPaths.news) ? 'news' : pathname === publicPaths.join ? 'join' : null;

  // A thin underline slides in on hover and stays under the current section.
  // Sizes match the HBAAC page's header (Plan AO), so moving between the two
  // pages keeps the logo, the menu and the button where they were.
  const navLink = (active: boolean) =>
    `relative py-1 text-[15px] font-semibold transition-colors after:absolute after:inset-x-0 after:-bottom-0.5 after:h-0.5 after:origin-left after:rounded-full after:bg-brand-500 after:transition-transform after:duration-300 hover:text-brand-700 hover:after:scale-x-100 dark:hover:text-brand-400 ${
      active ? 'text-brand-700 after:scale-x-100 dark:text-brand-400' : 'text-slate-600 after:scale-x-0 dark:text-slate-300'
    }`;

  return (
    <header
      // No backdrop blur: switched on at the first pixels of a scroll it popped in
      // while the colour faded, and a blurred header is re-rendered on every
      // frame something inside it moves. A near-opaque ground fades in instead.
      className={`sticky top-0 z-40 border-b transition-[background-color,border-color] duration-300 ${
        scrolled
          ? 'border-slate-200 bg-white/95 dark:border-navy-700 dark:bg-navy-900/95'
          : 'border-transparent bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-[1328px] items-center justify-between gap-4 px-4 md:h-[76px] md:px-6">
        <Link to={publicPaths.home} className="flex items-center gap-2.5">
          <img src="/logo/ava.webp" alt="" width={40} height={40} decoding="async" className="vt-logo h-10 w-10 rounded-full dark:hidden" />
          <img src="/logo/ava-white.webp" alt="" width={40} height={40} decoding="async" className="vt-logo hidden h-10 w-10 rounded-full dark:block" />
          <span className="font-display text-lg font-extrabold tracking-tight text-slate-800 dark:text-white">HBAC</span>
        </Link>

        <nav className="hidden items-center gap-6 md:flex lg:gap-8">
          {anchors.map((a) => (
            <NavTarget key={a.id} id={a.id} aria-current={current === a.id ? 'location' : undefined} className={navLink(current === a.id)}>
              {a.label}
            </NavTarget>
          ))}
          {/* The competition by its own logo (Plan AO): violet on the light header, white on the dark. */}
          <Link {...toHbaac} aria-label="HBAAC, cuộc thi của CLB" className={HBAAC_LOGO_LINK}>
            <img src="/logo/hbaac-lockup.png" alt="" width={139} height={44} decoding="async" className="h-9 w-auto dark:hidden" />
            <img src="/logo/hbaac-lockup-white.png" alt="" width={139} height={44} decoding="async" className="hidden h-9 w-auto dark:block" />
          </Link>
        </nav>

        <div className="flex items-center gap-1.5">
          <ThemeToggle className="text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-navy-800" />
          <Link
            to={token ? paths.portal : publicPaths.login}
            onPointerEnter={warmLogin}
            onFocus={warmLogin}
            className="hidden h-11 items-center gap-1.5 rounded-full bg-navy-900 px-5 text-sm font-bold text-white transition-colors hover:bg-navy-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:focus-visible:ring-brand-400 md:flex dark:bg-brand-700 dark:hover:bg-brand-800"
          >
            <LogIn size={15} /> Đăng nhập
          </Link>
          <button
            ref={toggle}
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Đóng menu' : 'Mở menu'}
            aria-expanded={open}
            className="flex h-11 w-11 items-center justify-center rounded-xl text-slate-600 hover:bg-slate-100 md:hidden dark:text-slate-300 dark:hover:bg-navy-800"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white px-4 py-3 md:hidden dark:border-navy-700 dark:bg-navy-900">
          <div className="flex flex-col gap-1">
            {anchors.map((a) => (
              <NavTarget
                key={a.id}
                id={a.id}
                onClick={() => setOpen(false)}
                className="rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-navy-800"
              >
                {a.label}
              </NavTarget>
            ))}
            <Link
              {...toHbaac}
              aria-label="HBAAC, cuộc thi của CLB"
              className="flex items-center rounded-xl px-3 py-2 hover:bg-slate-50 dark:hover:bg-navy-800"
            >
              <img src="/logo/hbaac-lockup.png" alt="" width={139} height={44} loading="lazy" decoding="async" className="h-8 w-auto dark:hidden" />
              <img src="/logo/hbaac-lockup-white.png" alt="" width={139} height={44} loading="lazy" decoding="async" className="hidden h-8 w-auto dark:block" />
            </Link>
            <Link
              to={token ? paths.portal : publicPaths.login}
            onPointerEnter={warmLogin}
            onFocus={warmLogin}
              className="mt-1 flex items-center justify-center gap-1.5 rounded-xl bg-navy-900 px-4 py-2.5 text-sm font-bold text-white dark:bg-brand-700"
            >
              <LogIn size={15} /> Đăng nhập
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
